/**
 * WHAT THE GAME WAS DOING WHEN IT WENT WRONG. The one owner of report context.
 *
 * 🚨 **WHY THIS EXISTS.** Alex, 2026-08-17: *"The in game bug reporter should copy the state and
 * page the game is on when a user clicks and fills it in."*
 *
 * 🔴 **AND THE REPORTER ITSELF HAD BEEN LOST.** Revival had an in-page bug reporter
 * (`web/bug-reporter.html`, 13 KB) that was injected into the UNITY shell's BODY by
 * `deploy.ps1 Set-IndexHead`. When the product became `client-web` it did not come across, and
 * nothing noticed, because every check we own looks for defects in what IS there. Measured against
 * the live host 2026-08-17: no `rv-bug-btn` in the page, no `rv-bug` in the bundle, and
 * `POST /api/bug` answered **404** - the route was gone server-side too. Meanwhile
 * `ui/betamark.ts` still told testers that reporting a bug was the most useful thing they could do.
 * **A feature deleted during a migration is invisible to every check that hunts for defects in what
 * exists.**
 *
 * ⭐ **A REGISTRY, NOT A REACH-IN.** The obvious build is for the reporter to import the match
 * screen and read its model. That model is a `const` inside `MatchScreen.mount`, so reaching it
 * would mean promoting it to a module global - a second owner of live match state, for the benefit
 * of a diagnostic. Instead each surface PUBLISHES a provider and the reporter merges whatever is
 * registered. A screen that publishes nothing simply contributes nothing.
 *
 * ⚠️ **EVERY PROVIDER IS CALLED INSIDE A try/catch AND MAY RETURN NOTHING.** A reporter that throws
 * while collecting context is a reporter that cannot report the crash it was opened for, which is
 * precisely when it is needed. A provider that fails contributes an `_error` note and the report
 * still goes.
 */

export type ContextProvider = () => unknown;

const providers = new Map<string, ContextProvider>();

/** The screen the router last showed. Owned here so the reporter never parses the address bar. */
let currentScreen = "";

/**
 * Recent client-side errors, oldest first.
 *
 * ⭐ **CAPTURED CONTINUOUSLY, NOT AT REPORT TIME.** By the time a player finds the button, the
 * console has usually already been cleared by a navigation, and asking them to open devtools is
 * asking them not to report. This is the half of a bug report a human cannot supply.
 *
 * ⚠️ BOUNDED. An error loop must not turn the reporter into a memory leak, so the buffer keeps the
 * most recent few and counts what it dropped rather than growing.
 */
const ERROR_CAP = 25;
const errors: { at: string; text: string }[] = [];
let errorsDropped = 0;

function note(text: string): void {
  errors.push({ at: new Date().toISOString().slice(11, 19), text: text.slice(0, 400) });
  while (errors.length > ERROR_CAP) {
    errors.shift();
    errorsDropped++;
  }
}

/**
 * Start listening for errors. Called once, from `main.ts`, as early as possible.
 *
 * ⚠️ **IT DOES NOT SWALLOW ANYTHING.** No `preventDefault`, no return value: the error still reaches
 * the console and any other handler. An instrument that changes the behaviour it observes is the
 * trap this project keeps paying for.
 */
export function installErrorCapture(): void {
  window.addEventListener("error", (e) => {
    const m = e.error instanceof Error ? `${e.error.name}: ${e.error.message}` : String(e.message);
    note(`${m} (${e.filename ?? "?"}:${e.lineno ?? 0})`);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r: unknown = e.reason;
    note(`unhandled rejection: ${r instanceof Error ? `${r.name}: ${r.message}` : String(r)}`);
  });
}

/**
 * A failed API call.
 *
 * 🔴 **THE ERROR LISTENERS ABOVE DO NOT SEE THESE, AND THIS IS THE CLASS THAT MATTERS MOST.**
 * Measured while testing this reporter, 2026-08-17: a real sign-in failure produced a report whose
 * error list was EMPTY, because the only failure was `400 Bad Request` on
 * `/api/v1/Identity/LoginGuest` - a rejected fetch, not a thrown JavaScript error, so neither
 * `window.onerror` nor `unhandledrejection` fires. **The single most diagnostic fact about the
 * failure was the one thing the instrument could not see**, and the report would have looked clean.
 *
 * ⭐ Recorded at the ONE place every call already goes through (`net/api.ts` `request`), so no call
 * site has to remember, and a new route is covered the day it is added.
 *
 * ⚠️ The server's own body text is kept, truncated. Its refusals are written for a player and are
 * usually the answer; a bare status code sends the reader back to the logs.
 */
export function noteApiFailure(method: string, path: string, status: number, body: string): void {
  note(`API ${status} ${method} ${path}${body ? ` - ${body.slice(0, 200)}` : ""}`);
}

/** The router calls this on every navigation. */
export function setScreen(id: string): void {
  currentScreen = id;
}

export function screen(): string {
  return currentScreen;
}

/**
 * Publish a named context provider. Calling again with the same name REPLACES it, which is what a
 * screen re-mounting should do; two live providers for one name would mean the report carried a
 * stale board from a match the player already left.
 */
export function registerContext(name: string, get: ContextProvider): void {
  providers.set(name, get);
}

/** Withdraw a provider. A screen MUST do this on unmount, or it reports a board that is gone. */
export function clearContext(name: string): void {
  providers.delete(name);
}

export interface Snapshot {
  at: string;
  screen: string;
  url: string;
  build: string;
  viewport: string;
  userAgent: string;
  errors: { at: string; text: string }[];
  errorsDropped: number;
  context: Record<string, unknown>;
}

/**
 * The build that is running.
 *
 * ⭐ **READ OFF THE LOADED BUNDLE, NOT FROM A CONSTANT WE BUMP.** A version string someone has to
 * remember to increment reports the last time somebody remembered. The hashed entry filename
 * changes on every real build by construction, so "which build was the player on" cannot drift.
 */
function buildId(): string {
  const s = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))
    .map((e) => e.src)
    .find((u) => /\/assets\/index-[^/]+\.js$/.test(u));
  return s ? (s.split("/").pop() ?? "unknown") : "dev";
}

export function snapshot(): Snapshot {
  const context: Record<string, unknown> = {};
  for (const [name, get] of providers) {
    try {
      context[name] = get();
    } catch (err) {
      // See the header: a failing provider must not cost us the report.
      context[name] = { _error: err instanceof Error ? err.message : String(err) };
    }
  }
  return {
    at: new Date().toISOString(),
    screen: currentScreen,
    url: location.href,
    build: buildId(),
    viewport: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio ?? 1}`,
    userAgent: navigator.userAgent,
    errors: errors.slice(),
    errorsDropped,
    context,
  };
}
